/**
 * Macro #25 Analytics — dependency-free SVG micro-charts (no chart library).
 *
 * Truthful, minimal, on-brand. Each renders an explicit empty state (via the
 * parent) — a chart is shown ONLY when it has real data.
 */
import { type TimeseriesPoint } from "../../lib/analytics-api";
import { usdc } from "../analytics-format";

function EmptyChart({ testId, label }: { testId: string; label: string }) {
  return (
    <div
      data-testid={`${testId}-empty`}
      className="flex h-28 items-center justify-center rounded-xl border border-white/8 bg-white/[0.02] text-[12px] text-white/40"
    >
      {label}
    </div>
  );
}

/** Cumulative / count area+line trend from [{t, v}]. */
export function AreaTrend({
  points,
  testId,
  tint = "#5eead4",
  emptyLabel = "No data in this range yet.",
}: {
  points: TimeseriesPoint[];
  testId: string;
  tint?: string;
  emptyLabel?: string;
}) {
  if (!points.length) return <EmptyChart testId={testId} label={emptyLabel} />;
  const vals = points.map((p) => p.v ?? 0);
  const max = Math.max(1, ...vals);
  const n = points.length;
  const xy = points.map((p, i) => {
    const x = n === 1 ? 50 : (i / (n - 1)) * 100;
    const y = 38 - ((p.v ?? 0) / max) * 34;
    return [x, y] as const;
  });
  const line = xy.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(2)},${y.toFixed(2)}`).join(" ");
  const area = `${line} L100,40 L0,40 Z`;
  return (
    <svg data-testid={testId} viewBox="0 0 100 40" preserveAspectRatio="none" className="h-28 w-full" role="img">
      <path d={area} fill={tint} fillOpacity="0.14" />
      <path d={line} fill="none" stroke={tint} strokeWidth="1.4" vectorEffect="non-scaling-stroke" />
      {xy.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="1.1" fill={tint} vectorEffect="non-scaling-stroke" />
      ))}
    </svg>
  );
}

/** Stacked referral/neighbor reward bars (micro-USDC) from [{t, referral_micro, neighbor_micro}]. */
export function StackedRewardBars({
  points,
  testId,
  emptyLabel = "No rewards in this range yet.",
}: {
  points: TimeseriesPoint[];
  testId: string;
  emptyLabel?: string;
}) {
  if (!points.length) return <EmptyChart testId={testId} label={emptyLabel} />;
  const totals = points.map((p) => (p.referral_micro ?? 0) + (p.neighbor_micro ?? 0));
  const max = Math.max(1, ...totals);
  const n = points.length;
  const slot = 100 / n;
  const bw = Math.min(8, slot * 0.6);
  return (
    <div>
      <svg data-testid={testId} viewBox="0 0 100 40" preserveAspectRatio="none" className="h-28 w-full" role="img">
        {points.map((p, i) => {
          const ref = p.referral_micro ?? 0;
          const nei = p.neighbor_micro ?? 0;
          const cx = slot * i + slot / 2 - bw / 2;
          const hRef = (ref / max) * 34;
          const hNei = (nei / max) * 34;
          return (
            <g key={i}>
              <rect x={cx} y={38 - hRef} width={bw} height={hRef} fill="#5eead4" />
              <rect x={cx} y={38 - hRef - hNei} width={bw} height={hNei} fill="#a78bfa" />
            </g>
          );
        })}
      </svg>
      <div className="mt-2 flex gap-4 font-mono text-[9px] uppercase tracking-[0.12em] text-white/45">
        <span className="inline-flex items-center gap-1"><i className="inline-block h-2 w-2 rounded-sm" style={{ background: "#5eead4" }} />Referral</span>
        <span className="inline-flex items-center gap-1"><i className="inline-block h-2 w-2 rounded-sm" style={{ background: "#a78bfa" }} />Neighbor</span>
        <span className="ml-auto text-white/35">
          {usdc(points.reduce((s, p) => s + (p.referral_micro ?? 0) + (p.neighbor_micro ?? 0), 0))} USDC in range
        </span>
      </div>
    </div>
  );
}
